/**
 * Edit Category Dialog Component
 * Form to edit an existing category with rename warning
 */

import { useState, useEffect } from 'react';
import { useUpdateCategory, useCategoryAffectedAssets } from '../../stores/categories';
import { useActiveUsersStore } from '../../stores/users';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RenameWarningDialog } from './RenameWarningDialog';
import type { Category } from '../../types/api';

interface EditCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
  onSuccess: () => void;
}

export function EditCategoryDialog({
  open,
  onOpenChange,
  category,
  onSuccess,
}: EditCategoryDialogProps) {
  const [categoryName, setCategoryName] = useState('');
  const [defaultResponsiblePersonId, setDefaultResponsiblePersonId] = useState<number | null>(null);
  const [defaultDisposalAvenue, setDefaultDisposalAvenue] = useState('');
  const [error, setError] = useState('');
  const [showRenameWarning, setShowRenameWarning] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<any>(null);
  const [checkingAssets, setCheckingAssets] = useState(false);

  const updateMutation = useUpdateCategory();
  useActiveUsersStore.live();
  const users = useActiveUsersStore(s => s.activeUsers);

  const [affectedData, setAffectedData] = useState<any>(null);
  const checkAffectedAssets = useCategoryAffectedAssets();

  useEffect(() => {
    if (category) {
      setCategoryName(category.category_name);
      setDefaultResponsiblePersonId(category.default_responsible_person_id);
      setDefaultDisposalAvenue(category.default_disposal_avenue || '');
    }
  }, [category]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!categoryName.trim()) {
      setError('Category name is required');
      return;
    }

    if (!category) return;

    const updateData = {
      category_name: categoryName.trim(),
      default_responsible_person_id: defaultResponsiblePersonId,
      default_disposal_avenue: defaultDisposalAvenue || null,
    };

    const nameChanged = categoryName.trim() !== category.category_name;

    if (nameChanged) {
      setCheckingAssets(true);
      setPendingUpdate(updateData);
      try {
        const result = await checkAffectedAssets.mutate({ categoryId: category.category_id, limit: 10 });
        setAffectedData(result);
        setCheckingAssets(false);
        if ((result as any).assetCount > 0) {
          setShowRenameWarning(true);
        } else {
          await performUpdate(updateData);
        }
      } catch (err) {
        setCheckingAssets(false);
        setError(err instanceof Error ? err.message : 'Failed to check affected assets');
      }
    } else {
      await performUpdate(updateData);
    }
  }


  async function performUpdate(data: any) {
    if (!category) return;

    try {
      await updateMutation.mutate({
        categoryId: category.category_id,
        data,
      });
      handleClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    }
  }

  async function handleRenameConfirm() {
    setShowRenameWarning(false);
    await performUpdate(pendingUpdate);
  }

  function handleRenameCancel() {
    setShowRenameWarning(false);
    setPendingUpdate(null);
    setAffectedData(null);
  }

  function handleClose() {
    if (!updateMutation.isPending && !checkingAssets) {
      setCategoryName('');
      setDefaultResponsiblePersonId(null);
      setDefaultDisposalAvenue('');
      setError('');
      setShowRenameWarning(false);
      setPendingUpdate(null);
      setAffectedData(null);
      onOpenChange(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>
              Update the category details. Renaming will update all associated assets.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name">Category Name *</Label>
                <Input
                  id="category-name"
                  type="text"
                  placeholder="e.g., Electronics, Furniture, Artwork"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  disabled={updateMutation.isPending || checkingAssets}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsible-person">Default Responsible Person</Label>
                <Select
                  value={defaultResponsiblePersonId?.toString() || 'none'}
                  onValueChange={(value) =>
                    setDefaultResponsiblePersonId(value === 'none' ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger disabled={updateMutation.isPending || checkingAssets}>
                    <SelectValue placeholder="Select a person (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users?.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id.toString()}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="disposal-avenue">Default Disposal Avenue</Label>
                <Input
                  id="disposal-avenue"
                  type="text"
                  placeholder="e.g., Auction, Online Marketplace"
                  value={defaultDisposalAvenue}
                  onChange={(e) => setDefaultDisposalAvenue(e.target.value)}
                  disabled={updateMutation.isPending || checkingAssets}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending || checkingAssets}>
                {checkingAssets ? 'Checking...' : updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {category && affectedData && (
        <RenameWarningDialog
          open={showRenameWarning}
          onOpenChange={setShowRenameWarning}
          entityType="category"
          oldName={category.category_name}
          newName={categoryName.trim()}
          affectedAssets={affectedData.sampleAssets}
          assetCount={affectedData.assetCount}
          onConfirm={handleRenameConfirm}
          onCancel={handleRenameCancel}
          isLoading={updateMutation.isPending}
        />
      )}
    </>
  );
}
